import { type StreamError } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { type RovoInstrumentedError } from '@atlassian/conversation-assistant-utils/httpUtils';

const generateTraceId = () =>
	`${Date.now().toString(16)}-${Math.floor(Math.random() * 1e4).toString(16)}`;

export class StreamErrorException extends Error implements RovoInstrumentedError {
	detail: Pick<StreamError, 'message' | 'metadata'> &
		Partial<Pick<StreamError, 'millisOffset' | 'type'>>;
	errorAttributes: RovoInstrumentedError['errorAttributes'];

	constructor(
		message: string,
		params: {
			detail: Pick<StreamError, 'message' | 'metadata'> &
				Partial<Pick<StreamError, 'millisOffset' | 'type'>>;
			traceId: string | undefined;
		},
	) {
		const { detail, traceId } = params;

		super(message);
		this.name = this.constructor.name;
		if (typeof Error.captureStackTrace === 'function') {
			Error.captureStackTrace(this, this.constructor);
		}

		this.detail = detail;

		this.errorAttributes = {
			errorCode: this.detail.message.message_template,
			statusCode: this.detail.message.status_code,
			rawErrorMessage: this.detail.message.content,
			// in case there is no traceId, we generate one in FE
			// and make it distinguishable from BE generated traceId
			// appended by random 5 digit number
			traceId: traceId ?? `StreamError-${generateTraceId()}`,
			requestId: this.detail.metadata?.request_id,
			latestStreamMessageType: this.detail.type,
			latestStreamMessageMillisOffset: this.detail.millisOffset,
		};
	}
}

/**
 * This error is specific to catch error while looping through the generator
 */
export class StreamGeneratorError extends Error implements RovoInstrumentedError {
	errorAttributes: RovoInstrumentedError['errorAttributes'];
	cause?: unknown;

	constructor(
		message: string,
		params: {
			detail?: {
				millisOffset?: number;
				type: string;
			};
			traceId: string | undefined;
		},
		cause?: unknown,
	) {
		super(message);
		this.name = this.constructor.name;
		this.cause = cause;
		this.errorAttributes = {
			traceId: params.traceId ?? `StreamGenerator-${generateTraceId()}`,
			latestStreamMessageType: params.detail?.type,
			latestStreamMessageMillisOffset: params.detail?.millisOffset,
		};
	}
}

export class NoFinalResponseError extends Error implements RovoInstrumentedError {
	errorAttributes: RovoInstrumentedError['errorAttributes'];

	constructor(
		message: string,
		params: {
			traceId: string | undefined;
		},
	) {
		super(message);
		this.name = this.constructor.name;
		this.errorAttributes = {
			traceId: params.traceId ?? `NoFinalResponse-${generateTraceId()}`,
		};
	}
}

interface RovoMessageActionError extends Error {
	errorAttributes: {
		conversationId: string;
		messageId: string;
		invocationId: string;
		actionKey: string;
	};
}

export class MessageActionError extends Error implements RovoMessageActionError {
	errorAttributes: RovoMessageActionError['errorAttributes'];
	cause?: Error;

	constructor(params: {
		message: string;
		conversationId: string;
		messageId: string;
		invocationId: string;
		actionKey: string;
		cause?: Error;
	}) {
		super(params.message);
		this.name = this.constructor.name;
		this.errorAttributes = {
			conversationId: params.conversationId,
			messageId: params.messageId,
			invocationId: params.invocationId,
			actionKey: params.actionKey,
		};
		if (params.cause) {
			this.cause = params.cause;
		}
	}
}

export class NoActionInvokedError extends Error implements RovoInstrumentedError {
	errorAttributes: RovoInstrumentedError['errorAttributes'];

	constructor(
		message: string,
		params: {
			traceId: string | undefined;
		},
	) {
		super(message);
		this.name = this.constructor.name;
		if (typeof Error.captureStackTrace === 'function') {
			Error.captureStackTrace(this, this.constructor);
		}

		this.errorAttributes = {
			traceId: params.traceId ?? `NoActionInvoked-${generateTraceId()}`,
		};
	}
}

export class DeepResearchError extends Error implements RovoInstrumentedError {
	errorAttributes: RovoInstrumentedError['errorAttributes'];
	message_template: string;

	constructor(
		message: string,
		params: {
			traceId?: string;
			requestId?: string;
		} = {},
	) {
		super(message);
		this.name = this.constructor.name;
		this.message_template = 'DEEP_RESEARCH_ERROR';

		this.errorAttributes = {
			errorCode: 'DEEP_RESEARCH_ERROR',
			traceId: params.traceId ?? `DeepResearch-${generateTraceId()}`,
			requestId: params.requestId,
		};
	}
}

/**
 * Error thrown when a stream needs to be reconnected due to a client-side streaming error.
 * This signals that the reconnectToInProgressStream action should be triggered.
 */
export class StreamDisconnectedError extends Error implements RovoInstrumentedError {
	errorAttributes: RovoInstrumentedError['errorAttributes'];

	readonly agentMessageId: string;
	readonly conversationId: string;
	readonly answerIndex: number;
	readonly lastEventId?: string;

	constructor(
		message: string,
		params: {
			agentMessageId: string;
			conversationId: string;
			answerIndex: number;
			lastEventId?: string;
			originalError: Error;
			traceId?: string;
			detail?: {
				millisOffset?: number;
				type: string;
			};
		},
	) {
		super(message);
		this.name = this.constructor.name;

		this.agentMessageId = params.agentMessageId;
		this.conversationId = params.conversationId;
		this.answerIndex = params.answerIndex;
		this.lastEventId = params.lastEventId;

		this.errorAttributes = {
			traceId: params.traceId ?? `StreamReconnection-${generateTraceId()}`,
			errorCode: 'STREAM_RECONNECTION_NEEDED',
			originalError: params.originalError.message,
			latestStreamMessageType: params.detail?.type,
			latestStreamMessageMillisOffset: params.detail?.millisOffset,
		};

		if (typeof Error.captureStackTrace === 'function') {
			Error.captureStackTrace(this, this.constructor);
		}
	}
}
